import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Collapse,
  Stack,
  Paper,
  Typography,
  Divider,
  Snackbar,
  Grid,
} from '@mui/material';
import {
  Trash2 as DeleteIcon,
  Copy as ContentCopyIcon,
  Plus as AddIcon,
  X as CloseIcon,
  Webhook as WebhookOutlinedIcon,
  Info as InfoIcon,
} from 'lucide-mui';
import DeleteDialog from './DeleteDialog';
import { Webhook } from '../../../../../types/state';
import LearnMore from './LearnMore';
import { ToggleSetting } from '../../../common';

interface WebhooksProps {
  webhooks: Webhook[];
  onAddWebhook: (name: string, url: string) => Promise<void>;
  onRemoveWebhook: (url: string) => Promise<void>;
  note?: React.ReactNode;
}

const SNACKBAR_DURATION = 4000;

const Webhooks: React.FC<WebhooksProps> = ({
  webhooks,
  onAddWebhook,
  onRemoveWebhook,
  note,
}) => {
  const [formState, setFormState] = useState({
    newWebhookUrl: '',
    newWebhookName: '',
    snackbarOpen: false,
    snackbarMessage: '',
    deleteDialogOpen: false,
    webhookToDelete: null as Webhook | null,
    showAddWebhook: false,
  });

  const [learnMoreOpen, setLearnMoreOpen] = useState(false);

  const updateFormState = (updates: Partial<typeof formState>) => {
    setFormState((prev) => ({ ...prev, ...updates }));
  };

  const handleAddWebhook = async () => {
    const { newWebhookUrl, newWebhookName } = formState;

    if (!newWebhookUrl || !newWebhookName) {
      updateFormState({
        snackbarMessage: 'Please fill in both name and URL fields',
        snackbarOpen: true,
      });
      return;
    }

    if (!newWebhookUrl.startsWith('https://')) {
      updateFormState({
        snackbarMessage: 'Webhook URL must start with https://',
        snackbarOpen: true,
      });
      return;
    }

    try {
      await onAddWebhook(newWebhookName, newWebhookUrl);
      updateFormState({
        newWebhookUrl: '',
        newWebhookName: '',
        snackbarMessage: 'Webhook added successfully',
        snackbarOpen: true,
        showAddWebhook: false,
      });
    } catch (error) {
      updateFormState({
        snackbarMessage: `Failed to add webhook: ${error.message}`,
        snackbarOpen: true,
      });
    }
  };

  const handleRemoveWebhook = (url: string, name: string) => {
    updateFormState({
      webhookToDelete: { url, name },
      deleteDialogOpen: true,
    });
  };

  const handleConfirmDelete = async () => {
    const { webhookToDelete } = formState;
    if (!webhookToDelete) return;

    try {
      await onRemoveWebhook(webhookToDelete.url);
      updateFormState({
        snackbarMessage: 'Webhook removed successfully',
        snackbarOpen: true,
        deleteDialogOpen: false,
        webhookToDelete: null,
      });
    } catch (error) {
      updateFormState({
        snackbarMessage: `Failed to remove webhook: ${error.message}`,
        snackbarOpen: true,
      });
    }
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    updateFormState({
      snackbarMessage: `${label} copied to clipboard`,
      snackbarOpen: true,
    });
  };

  const toggleState = (key: keyof typeof formState) => {
    updateFormState({ [key]: !formState[key] });
  };

  const handleClose = (key: keyof typeof formState) => {
    updateFormState({ [key]: false });
  };

  return (
    <Box sx={{ mb: 3 }}>
      <ToggleSetting
        checked={false}
        onChange={() => setLearnMoreOpen(true)}
        label="Learn More"
        description="Understand how to use webhooks to receive notifications"
        icon={<InfoIcon />}
        rightElement={
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setLearnMoreOpen(true)}
          >
            Learn More
          </Button>
        }
      />

      <LearnMore
        open={learnMoreOpen}
        onClose={() => setLearnMoreOpen(false)}
        note={note}
      />

      {webhooks.length > 0 ? (
        <Paper variant="outlined" sx={{ borderColor: 'divider', mt: 2 }}>
          <List disablePadding>
            {webhooks.map((webhook, index) => (
              <React.Fragment key={webhook.url}>
                <ListItem
                  secondaryAction={
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Copy webhook URL">
                        <IconButton
                          onClick={() => handleCopy(webhook.url, 'Webhook URL')}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete webhook">
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() =>
                            handleRemoveWebhook(webhook.url, webhook.name)
                          }
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  }
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <WebhookOutlinedIcon
                          sx={{
                            width: 20,
                            height: 20,
                            color: 'primary.main',
                          }}
                        />
                        <Typography variant="subtitle1" noWrap>
                          {webhook.name}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          wordBreak: 'break-all',
                          pr: 16,
                        }}
                      >
                        {webhook.url}
                      </Typography>
                    }
                  />
                </ListItem>
                {index < webhooks.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 4,
            mt: 2,
            bgcolor: 'action.hover',
            borderRadius: 1,
          }}
        >
          <WebhookOutlinedIcon
            sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}
          />
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No webhooks configured yet
          </Typography>
        </Box>
      )}

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          startIcon={formState.showAddWebhook ? <CloseIcon /> : <AddIcon />}
          onClick={() => toggleState('showAddWebhook')}
          size="small"
          variant={formState.showAddWebhook ? 'outlined' : 'contained'}
          color={formState.showAddWebhook ? 'secondary' : 'primary'}
        >
          {formState.showAddWebhook ? 'Cancel' : 'Add Webhook'}
        </Button>
      </Box>

      <Collapse in={formState.showAddWebhook}>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={4}>
              <TextField
                label="Webhook Name"
                placeholder="e.g., Production Server"
                variant="outlined"
                value={formState.newWebhookName}
                onChange={(e) =>
                  updateFormState({ newWebhookName: e.target.value })
                }
                fullWidth
                helperText="A descriptive name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Webhook URL"
                placeholder="https://your-server.com/webhook"
                variant="outlined"
                value={formState.newWebhookUrl}
                onChange={(e) =>
                  updateFormState({ newWebhookUrl: e.target.value })
                }
                fullWidth
                helperText="Must be a secure HTTPS endpoint"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddWebhook}
                fullWidth
                disabled={!formState.newWebhookUrl || !formState.newWebhookName}
                sx={{ height: { xs: '40px', md: '56px' } }}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Collapse>

      <Snackbar
        open={formState.snackbarOpen}
        autoHideDuration={SNACKBAR_DURATION}
        onClose={() => handleClose('snackbarOpen')}
        message={formState.snackbarMessage}
      />

      <DeleteDialog
        open={formState.deleteDialogOpen}
        webhookName={formState.webhookToDelete?.name || ''}
        onClose={() => handleClose('deleteDialogOpen')}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};

export default Webhooks;
